import React, { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Button, Chip, CircularProgress, Alert, Typography, Paper,
  Grid, Stack, Drawer, TextField, MenuItem, Select, FormControl,
  InputLabel, InputAdornment, FormHelperText, Dialog, DialogTitle, DialogContent, DialogContentText,
  DialogActions, IconButton, Divider, Tooltip, Tab, Tabs, Fade,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SearchIcon from '@mui/icons-material/Search';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import { DataGrid } from '@mui/x-data-grid';
import { partidasAPI, pedidosAPI, personasAPI } from '../services/api';

// ─── Constants ───────────────────────────────────────────────────────────────

const ESTADO_CHIP_STYLE = {
  recepcionado:       { bg: '#e8f5e9', color: '#1b5e20', border: '#a5d6a7' },
  en_curso:           { bg: '#e3f2fd', color: '#0d47a1', border: '#90caf9' },
  comprometido:       { bg: '#ede7f6', color: '#4527a0', border: '#b39ddb' },
  probabilidad_media: { bg: '#fff8e1', color: '#e65100', border: '#ffcc80' },
  probabilidad_baja:  { bg: '#fce4ec', color: '#b71c1c', border: '#ef9a9a' },
};

const ESTADO_OPCIONES = [
  { value: '', label: 'Todos' },
  { value: 'comprometido', label: 'Comprometido' },
  { value: 'en_curso', label: 'En curso' },
  { value: 'probabilidad_baja', label: 'Prob. baja' },
  { value: 'probabilidad_media', label: 'Prob. media' },
  { value: 'recepcionado', label: 'Recepcionado' },
];

const CANAL_OPCIONES = [
  { value: 'amazon', label: 'Amazon' },
  { value: 'procure', label: 'Procur-E' },
  { value: 'pyf', label: 'PyF' },
];

const TIPO_OPCIONES = [
  { value: 'pedido_usuario', label: 'Pedido de usuario' },
  { value: 'proveedor_singular', label: 'Proveedor singular' },
  { value: 'punchout', label: 'Punchout' },
];

const FORM_INITIAL = {
  nombre: '',
  proveedor: '',
  importe: '',
  estado: 'probabilidad_media',
  area_id: '',
  partida: '',
  canal: 'pyf',
  tipo: 'pedido_usuario',
  solicitado_por: '',
  fecha: '',
  no_solicitud: '',
  no_pedido: '',
  no_recepcion: '',
};

const fmt = (v) =>
  Number(v || 0).toLocaleString('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    useGrouping: true,
  });

// ─── TabPanel ───────────────────────────────────────────────────────────────

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`presupuesto-tabpanel-${index}`}
    aria-labelledby={`presupuesto-tab-${index}`}
    {...other}
  >
    {value === index && (
      <Fade in timeout={300}>
        <Box>{children}</Box>
      </Fade>
    )}
  </div>
);

// ─── KpiCard ─────────────────────────────────────────────────────────────────

const KpiCard = ({ label, value, color }) => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      textAlign: 'center',
      border: '1px solid #e8e8e8',
      borderTop: `4px solid ${color}`,
      transition: 'box-shadow 0.2s',
      '&:hover': { boxShadow: '0 2px 12px rgba(0,0,0,0.08)' },
    }}
  >
    <Typography
      variant="body2"
      color="text.secondary"
      display="block"
      mb={1}
      sx={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 500 }}
    >
      {label}
    </Typography>
    <Typography variant="h5" fontWeight={700} color={color} lineHeight={1}>
      {value}
    </Typography>
  </Paper>
);

KpiCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
};

// ─── BudgetBar ────────────────────────────────────────────────────────────────

/**
 * Barra con 2 bloques conceptuales:
 * - Consumido (verde)
 * - Pendiente (subdividido en comprometido / prob. media / prob. baja)
 */
const BudgetBar = ({ presupuesto, consumido, comprometido, probMedia, probBaja }) => {
  const safePresupuesto = Math.max(0, Number(presupuesto || 0));
  const safeConsumido = Math.max(0, Number(consumido || 0));
  const safeComprometido = Math.max(0, Number(comprometido || 0));
  const safeProbMedia = Math.max(0, Number(probMedia || 0));
  const safeProbBaja = Math.max(0, Number(probBaja || 0));

  const pendienteTotal = Math.max(0, safePresupuesto - safeConsumido);
  const pedidoPendienteTotal = safeComprometido + safeProbMedia + safeProbBaja;

  // Si los importes pendientes superan el disponible, se escalan para no desbordar.
  const scalePendiente =
    pedidoPendienteTotal > 0
      ? Math.min(1, pendienteTotal / pedidoPendienteTotal)
      : 0;

  const comprometidoEscalado = safeComprometido * scalePendiente;
  const probMediaEscalada = safeProbMedia * scalePendiente;
  const probBajaEscalada = safeProbBaja * scalePendiente;

  const pct = (v) => (safePresupuesto > 0 ? Math.min(100, (v / safePresupuesto) * 100) : 0);
  const pctC = pct(safeConsumido);
  const pctK = pct(comprometidoEscalado);
  const pctM = pct(probMediaEscalada);
  const pctB = pct(probBajaEscalada);
  const pctPendienteSinAsignar = Math.max(0, 100 - pctC - pctK - pctM - pctB);

  return (
    <Box sx={{ height: 7, bgcolor: '#e8e8e8', borderRadius: 4, overflow: 'hidden', display: 'flex' }}>
      {pctC > 0 && <Box sx={{ width: `${pctC}%`, height: '100%', bgcolor: '#2e7d32', transition: 'width 0.4s' }} />}
      {pctK > 0 && <Box sx={{ width: `${pctK}%`, height: '100%', bgcolor: '#1565c0', transition: 'width 0.4s' }} />}
      {pctM > 0 && <Box sx={{ width: `${pctM}%`, height: '100%', bgcolor: '#f57f17', transition: 'width 0.4s' }} />}
      {pctB > 0 && <Box sx={{ width: `${pctB}%`, height: '100%', bgcolor: '#c62828', transition: 'width 0.4s' }} />}
      {pctPendienteSinAsignar > 0 && (
        <Box sx={{ width: `${pctPendienteSinAsignar}%`, height: '100%', bgcolor: '#d7dce3', transition: 'width 0.4s' }} />
      )}
    </Box>
  );
};

BudgetBar.propTypes = {
  presupuesto: PropTypes.number.isRequired,
  consumido: PropTypes.number.isRequired,
  comprometido: PropTypes.number.isRequired,
  probMedia: PropTypes.number.isRequired,
  probBaja: PropTypes.number.isRequired,
};

// ─── PartidaTreeItem ──────────────────────────────────────────────────────────

const PartidaTreeItem = ({ partida, nivel, expandAllAction, expandAllVersion }) => {
  const [open, setOpen] = useState(false);
  const tieneHijos = partida.hijos?.length > 0;

  const presupuesto = Number(partida.presupuesto || 0);
  const consumido = Number(partida.presupuesto_consumido || 0);
  const comprometido = Number(partida.importe_comprometido || 0);
  const probMedia = Number(partida.importe_probabilidad_media || 0);
  const probBaja = Number(partida.importe_probabilidad_baja || 0);
  const disponible = Number(
    partida.disponible_comprometido ?? Math.max(0, presupuesto - consumido - comprometido)
  );

  const totalPedidos = consumido + comprometido + probMedia + probBaja;
  const pctUsado = presupuesto > 0 ? (totalPedidos / presupuesto) * 100 : 0;
  const statusColor = disponible < 0 ? '#c62828' : '#388e3c';
  const isArea = nivel === 0;

  const handleClick = () => {
    if (tieneHijos) setOpen((o) => !o);
  };

  useEffect(() => {
    if (!tieneHijos) return;
    if (expandAllAction === 'expand') setOpen(true);
    if (expandAllAction === 'collapse') setOpen(false);
  }, [expandAllAction, expandAllVersion, tieneHijos]);

  const tooltipContent = (
    <Stack spacing={0.3} sx={{ p: 0.5, minWidth: 210 }}>
      <Typography variant="caption" fontWeight={700} sx={{ fontSize: 11, pb: 0.5, borderBottom: '1px solid rgba(255,255,255,0.15)' }}>
        {partida.nombre}
      </Typography>
      <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Typography variant="caption" color="rgba(255,255,255,0.7)">Presupuesto</Typography>
        <Typography variant="caption" fontWeight={600}>{fmt(presupuesto)}</Typography>
      </Stack>
      <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Typography variant="caption" sx={{ color: '#81c784' }}>Consumido</Typography>
        <Typography variant="caption" fontWeight={600} sx={{ color: '#81c784' }}>{fmt(consumido)}</Typography>
      </Stack>
      <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Typography variant="caption" sx={{ color: '#90caf9' }}>Comprometido</Typography>
        <Typography variant="caption" fontWeight={600} sx={{ color: '#90caf9' }}>{fmt(comprometido)}</Typography>
      </Stack>
      <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Typography variant="caption" sx={{ color: '#ffcc80' }}>Prob. media</Typography>
        <Typography variant="caption" fontWeight={600} sx={{ color: '#ffcc80' }}>{fmt(probMedia)}</Typography>
      </Stack>
      <Stack direction="row" justifyContent="space-between" spacing={2}>
        <Typography variant="caption" sx={{ color: '#ef9a9a' }}>Prob. baja</Typography>
        <Typography variant="caption" fontWeight={600} sx={{ color: '#ef9a9a' }}>{fmt(probBaja)}</Typography>
      </Stack>
      <Stack direction="row" justifyContent="space-between" spacing={2} sx={{ pt: 0.5, borderTop: '1px solid rgba(255,255,255,0.15)' }}>
        <Typography variant="caption" sx={{ color: '#a5d6a7', fontWeight: 600 }}>Disponible (tras comp.)</Typography>
        <Typography variant="caption" fontWeight={700} sx={{ color: '#a5d6a7' }}>{fmt(disponible)}</Typography>
      </Stack>
    </Stack>
  );

  const areaMetrics = [
    { label: 'Presupuesto', value: presupuesto, color: 'text.secondary' },
    { label: 'Consumido', value: consumido, color: '#2e7d32' },
    { label: 'Comprometido', value: comprometido, color: '#1565c0' },
    { label: 'Prob. media', value: probMedia, color: '#e65100' },
    { label: 'Prob. baja', value: probBaja, color: '#b71c1c' },
    { label: 'Disponible', value: disponible, color: statusColor },
  ];

  const hojaMetrics = [
    { label: 'Presupuesto', value: presupuesto, color: 'text.secondary' },
    { label: 'Consumido', value: consumido, color: '#2e7d32' },
    { label: 'Comprometido', value: comprometido, color: '#1565c0' },
    { label: 'Prob. media', value: probMedia, color: '#e65100' },
    { label: 'Prob. baja', value: probBaja, color: '#b71c1c' },
    { label: 'Disponible', value: disponible, color: statusColor },
  ];

  return (
    <>
      <Box
        onClick={handleClick}
        sx={{
          pl: `${8 + nivel * 24}px`,
          pr: 2.5,
          py: isArea ? 1.25 : 0.875,
          cursor: tieneHijos ? 'pointer' : 'default',
          borderBottom: '1px solid',
          borderColor: isArea ? '#eaeef8' : '#f5f5f5',
          bgcolor: isArea ? '#f8f9fd' : 'white',
          borderLeft: `3px solid ${isArea ? statusColor : 'transparent'}`,
          '&:hover': {
            bgcolor: tieneHijos ? (isArea ? '#eef0fb' : '#fafafa') : undefined,
          },
          transition: 'background 0.15s',
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          {/* Expand chevron */}
          <Box
            sx={{
              flexShrink: 0,
              width: 20,
              height: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: tieneHijos ? (isArea ? '#3949ab' : '#bdbdbd') : 'transparent',
              transition: 'transform 0.2s ease',
              transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
            }}
          >
            {tieneHijos && <ChevronRightIcon sx={{ fontSize: 18 }} />}
          </Box>

          {/* Name + code */}
          <Box flex={1} minWidth={0}>
            <Typography
              variant="body2"
              fontWeight={isArea ? 700 : 500}
              color={isArea ? '#1a237e' : 'text.primary'}
              noWrap
              sx={{ fontSize: isArea ? 13 : 12, lineHeight: 1.4 }}
            >
              {partida.nombre}
            </Typography>
            <Typography variant="caption" color="text.disabled" sx={{ fontSize: 10, lineHeight: 1 }}>
              {partida.codigo}
            </Typography>
          </Box>

          {/* Metrics — hidden on xs to avoid overflow */}
          <Stack
            direction="row"
            spacing={isArea ? 1.5 : 1}
            sx={{ flexShrink: 0, display: { xs: 'none', sm: 'flex' } }}
          >
            {(isArea ? areaMetrics : hojaMetrics).map(({ label, value, color }) => (
              <Box key={label} textAlign="right" sx={{ minWidth: isArea ? 68 : 58 }}>
                <Typography
                  variant="caption"
                  color="text.disabled"
                  display="block"
                  sx={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: 0.3, lineHeight: 1.3 }}
                >
                  {label}
                </Typography>
                <Typography
                  variant="caption"
                  fontWeight={isArea ? 700 : 600}
                  sx={{ fontSize: isArea ? 11 : 10, color }}
                >
                  {fmt(value)}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Stack>

        {/* Budget bar with tooltip */}
        <Tooltip title={tooltipContent} placement="top" arrow>
          <Box sx={{ mt: 0.75, ml: '32px' }}>
            <BudgetBar
              presupuesto={presupuesto}
              consumido={consumido}
              comprometido={comprometido}
              probMedia={probMedia}
              probBaja={probBaja}
            />
          </Box>
        </Tooltip>
      </Box>

      {tieneHijos &&
        open &&
        partida.hijos.map((h) => (
          <PartidaTreeItem
            key={h.id}
            partida={h}
            nivel={nivel + 1}
            expandAllAction={expandAllAction}
            expandAllVersion={expandAllVersion}
          />
        ))}
    </>
  );
};

PartidaTreeItem.propTypes = {
  partida: PropTypes.shape({
    id: PropTypes.number.isRequired,
    codigo: PropTypes.string.isRequired,
    nombre: PropTypes.string.isRequired,
    presupuesto: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    presupuesto_consumido: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    importe_comprometido: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    importe_probabilidad_media: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    importe_probabilidad_baja: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    disponible_comprometido: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    disponible_probabilidad_media: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    disponible_probabilidad_baja: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    hijos: PropTypes.array,
  }).isRequired,
  nivel: PropTypes.number.isRequired,
  expandAllAction: PropTypes.oneOf(['expand', 'collapse', null]),
  expandAllVersion: PropTypes.number,
};

// ─── PedidoDrawer ─────────────────────────────────────────────────────────────

const PedidoDrawer = ({ open, onClose, pedido, hojas, areasData, onSaved }) => {
  const [form, setForm] = useState(FORM_INITIAL);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [personas, setPersonas] = useState([]);
  const [loadingPersonas, setLoadingPersonas] = useState(false);

  const esEdicion = Boolean(pedido);

  // Load personas lazily when drawer opens
  useEffect(() => {
    if (!open) return;
    if (personas.length > 0) return; // already loaded
    setLoadingPersonas(true);
    personasAPI
      .getAll({ activo: true })
      .then((res) => {
        const lista = res.data.results ?? res.data;
        setPersonas([...lista].sort((a, b) =>
          `${a.nombre} ${a.apellido}`.localeCompare(`${b.nombre} ${b.apellido}`, 'es')
        ));
      })
      .catch(() => {})
      .finally(() => setLoadingPersonas(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (open) {
      if (pedido) {
        setForm({
          nombre: pedido.nombre || '',
          proveedor: pedido.proveedor || '',
          importe: pedido.importe || '',
          estado: pedido.estado || 'probabilidad_media',
          area_id: pedido.tecnologia_id ? String(pedido.tecnologia_id) : '',
          partida: pedido.partida || '',
          canal: pedido.canal || 'pyf',
          tipo: pedido.tipo || 'pedido_usuario',
          solicitado_por: pedido.solicitado_por || '',
          fecha: pedido.fecha || '',
          no_solicitud: pedido.no_solicitud || '',
          no_pedido: pedido.no_pedido || '',
          no_recepcion: pedido.no_recepcion || '',
        });
      } else {
        setForm(FORM_INITIAL);
      }
      setErrors({});
      setSaveError(null);
    }
  }, [pedido, open]);

  const set = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setErrors((prev) => ({ ...prev, [field]: null }));
  };

  const validar = () => {
    const err = {};
    if (!form.nombre.trim()) err.nombre = 'El nombre es obligatorio';
    if (!form.importe || isNaN(Number(form.importe))) err.importe = 'Introduce un importe válido';
    if (!form.partida) err.partida = 'Selecciona una partida';
    return err;
  };

  const handleSubmit = async () => {
    const err = validar();
    if (Object.keys(err).length) {
      setErrors(err);
      return;
    }
    setSaving(true);
    setSaveError(null);
    const payload = {
      ...form,
      importe: Number(form.importe),
      solicitado_por: form.solicitado_por || null,
      fecha: form.fecha || null,
    };
    delete payload.area_id;
    try {
      let result;
      if (esEdicion) {
        const res = await pedidosAPI.partialUpdate(pedido.id, payload);
        result = res.data;
      } else {
        const res = await pedidosAPI.create(payload);
        result = res.data;
      }
      onSaved(result, esEdicion);
      onClose();
    } catch (err) {
      const serverErrors = err.response?.data;
      if (serverErrors && typeof serverErrors === 'object') {
        setErrors(
          Object.fromEntries(
            Object.entries(serverErrors).map(([k, v]) => [k, Array.isArray(v) ? v.join(' ') : v])
          )
        );
      } else {
        setSaveError('Error al guardar el pedido. Inténtalo de nuevo.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 560 },
          boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
        },
      }}
    >
      {/* Estado color strip */}
      <Box sx={{ height: 4, bgcolor: ESTADO_CHIP_STYLE[form.estado]?.border || '#e0e0e0', flexShrink: 0 }} />

      <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100% - 4px)', overflow: 'hidden' }}>

        {/* Header */}
        <Box sx={{ px: 3, pt: 2.5, pb: 2, borderBottom: '1px solid #eaeef8', flexShrink: 0 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
            <Box flex={1} minWidth={0}>
              <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                {esEdicion ? 'Editar pedido' : 'Nuevo pedido'}
              </Typography>
              {esEdicion && pedido?.nombre && (
                <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ mt: 0.25, fontSize: 12 }}>
                  {pedido.nombre}
                </Typography>
              )}
            </Box>
            <IconButton onClick={onClose} size="small" sx={{ ml: 1, mt: -0.5 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>

        {saveError && (
          <Alert severity="error" sx={{ mx: 3, mt: 2 }} onClose={() => setSaveError(null)}>
            {saveError}
          </Alert>
        )}

        {/* Form — scrollable */}
        <Box flex={1} overflow="auto" sx={{ px: 3, py: 2.5 }}>
          <Grid container spacing={2}>

            {/* ── Identificación ── */}
            <Grid size={12}>
              <Typography variant="overline" sx={{ fontSize: 10, fontWeight: 700, color: '#9e9e9e', letterSpacing: 1, lineHeight: 1 }}>
                Identificación
              </Typography>
            </Grid>

            <Grid size={12}>
              <TextField
                label="Nombre *"
                fullWidth
                value={form.nombre}
                onChange={set('nombre')}
                error={!!errors.nombre}
                helperText={errors.nombre}
                autoFocus
                placeholder="Descripción corta del pedido"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 7 }}>
              <FormControl fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={form.estado}
                  label="Estado"
                  onChange={set('estado')}
                  renderValue={(val) => {
                    const s = ESTADO_CHIP_STYLE[val] || {};
                    const opt = ESTADO_OPCIONES.find((o) => o.value === val);
                    return (
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.border, flexShrink: 0 }} />
                        <span>{opt?.label}</span>
                      </Stack>
                    );
                  }}
                >
                  {ESTADO_OPCIONES.filter((o) => o.value).map((opt) => {
                    const s = ESTADO_CHIP_STYLE[opt.value] || {};
                    return (
                      <MenuItem key={opt.value} value={opt.value}>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0, bgcolor: s.border }} />
                          <Typography variant="body2">{opt.label}</Typography>
                        </Stack>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 5 }}>
              <TextField
                label="Importe *"
                fullWidth
                type="number"
                value={form.importe}
                onChange={set('importe')}
                error={!!errors.importe}
                helperText={errors.importe}
                inputProps={{ min: 0, step: '0.01' }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">€</InputAdornment>,
                }}
              />
            </Grid>

            {/* ── Clasificación ── */}
            <Grid size={12}>
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="overline" sx={{ fontSize: 10, fontWeight: 700, color: '#9e9e9e', letterSpacing: 1, lineHeight: 1 }}>
                  Clasificación
                </Typography>
              </Box>
            </Grid>

            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>Tecnología *</InputLabel>
                <Select
                  value={form.area_id}
                  label="Tecnología *"
                  onChange={(e) => {
                    setForm((prev) => ({ ...prev, area_id: e.target.value, partida: '' }));
                    setErrors((prev) => ({ ...prev, partida: null }));
                  }}
                >
                  {[...areasData].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es')).map((a) => (
                    <MenuItem key={a.id} value={String(a.id)}>{a.nombre}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={12}>
              <FormControl fullWidth error={!!errors.partida} disabled={!form.area_id}>
                <InputLabel>Partida *</InputLabel>
                <Select value={form.partida} label="Partida *" onChange={set('partida')}>
                  {hojas
                    .filter((h) => String(h.padre) === form.area_id)
                    .sort((a, b) => a.codigo.localeCompare(b.codigo, 'es'))
                    .map((h) => (
                      <MenuItem key={h.id} value={h.id}>
                        <Box>
                          <Typography variant="caption" display="block" sx={{ fontSize: 10, color: '#9e9e9e', fontFamily: 'monospace', lineHeight: 1.2 }}>
                            {h.codigo}
                          </Typography>
                          <Typography variant="body2" sx={{ lineHeight: 1.4 }}>{h.nombre}</Typography>
                        </Box>
                      </MenuItem>
                    ))}
                </Select>
                <FormHelperText>
                  {errors.partida || (!form.area_id ? 'Selecciona primero una tecnología' : '')}
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid size={12}>
              <TextField
                label="Proveedor"
                fullWidth
                value={form.proveedor}
                onChange={set('proveedor')}
                placeholder="Nombre del proveedor"
              />
            </Grid>

            {/* ── Gestión ── */}
            <Grid size={12}>
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="overline" sx={{ fontSize: 10, fontWeight: 700, color: '#9e9e9e', letterSpacing: 1, lineHeight: 1 }}>
                  Gestión
                </Typography>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Canal</InputLabel>
                <Select value={form.canal} label="Canal" onChange={set('canal')}>
                  {CANAL_OPCIONES.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Tipo</InputLabel>
                <Select value={form.tipo} label="Tipo" onChange={set('tipo')}>
                  {TIPO_OPCIONES.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Solicitado por</InputLabel>
                <Select value={form.solicitado_por} label="Solicitado por" onChange={set('solicitado_por')}>
                  <MenuItem value=""><em>— Sin asignar —</em></MenuItem>
                  {loadingPersonas ? (
                    <MenuItem disabled>
                      <CircularProgress size={14} sx={{ mr: 1 }} /> Cargando…
                    </MenuItem>
                  ) : (
                    personas.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.nombre} {p.apellido}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Fecha"
                fullWidth
                type="date"
                value={form.fecha}
                onChange={set('fecha')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* ── Números de referencia ── */}
            <Grid size={12}>
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="overline" sx={{ fontSize: 10, fontWeight: 700, color: '#9e9e9e', letterSpacing: 1, lineHeight: 1 }}>
                  Números de referencia
                </Typography>
              </Box>
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Nº Solicitud" fullWidth value={form.no_solicitud} onChange={set('no_solicitud')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Nº Pedido" fullWidth value={form.no_pedido} onChange={set('no_pedido')} />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField label="Nº Recepción" fullWidth value={form.no_recepcion} onChange={set('no_recepcion')} />
            </Grid>

          </Grid>
        </Box>

        {/* Footer */}
        <Box sx={{ px: 3, pb: 3, pt: 2, borderTop: '1px solid #eaeef8', flexShrink: 0 }}>
          <Stack direction="row" spacing={1.5}>
            <Button
              fullWidth
              variant="outlined"
              onClick={onClose}
              disabled={saving}
              sx={{ borderColor: '#d0d5dd', color: 'text.secondary', fontWeight: 600 }}
            >
              Cancelar
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmit}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{ fontWeight: 700 }}
            >
              {esEdicion ? 'Guardar cambios' : 'Crear pedido'}
            </Button>
          </Stack>
        </Box>

      </Box>
    </Drawer>
  );
};

PedidoDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  pedido: PropTypes.object,
  hojas: PropTypes.array.isRequired,
  areasData: PropTypes.array.isRequired,
  onSaved: PropTypes.func.isRequired,
};

// ─── Main page ────────────────────────────────────────────────────────────────

const ControlPresupuesto = () => {
  const [arbol, setArbol] = useState([]);
  const [arbolError, setArbolError] = useState(null);
  const [estadisticas, setEstadisticas] = useState(null);
  const [pedidos, setPedidos] = useState([]);
  const [hojas, setHojas] = useState([]);
  const [areasData, setAreasData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroPartida, setFiltroPartida] = useState('');
  const [filtroSolicitadoPor, setFiltroSolicitadoPor] = useState('');
  const [filtroBusqueda, setFiltroBusqueda] = useState('');
  const [filtroCanal, setFiltroCanal] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroProveedor, setFiltroProveedor] = useState('');

  // Drawer (new / edit)
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingPedido, setEditingPedido] = useState(null);

  // Delete dialog
  const [pedidoAEliminar, setPedidoAEliminar] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Tabs
  const [activeTab, setActiveTab] = useState(0);

  // Tree expand/collapse controls
  const [expandAllAction, setExpandAllAction] = useState(null);
  const [expandAllVersion, setExpandAllVersion] = useState(0);

  // ── Load all data on mount ─────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const [treeRes, statsRes, hojasRes, pedidosRes, areasRes] = await Promise.allSettled([
        partidasAPI.getTree(),
        pedidosAPI.getEstadisticas(),
        partidasAPI.getHojas(),
        pedidosAPI.getAll(),
        partidasAPI.getAreas(),
      ]);
      if (treeRes.status === 'fulfilled') {
        setArbol(treeRes.value.data);
        setArbolError(null);
      } else {
        const status = treeRes.reason?.response?.status;
        const detail = treeRes.reason?.response?.data?.detail || treeRes.reason?.message || 'Error desconocido';
        setArbolError(`Error ${status ?? ''}: ${detail}`);
      }
      if (statsRes.status === 'fulfilled') setEstadisticas(statsRes.value.data);
      if (hojasRes.status === 'fulfilled') setHojas(hojasRes.value.data);
      if (pedidosRes.status === 'fulfilled')
        setPedidos(pedidosRes.value.data.results ?? pedidosRes.value.data);
      if (areasRes.status === 'fulfilled') setAreasData(areasRes.value.data);
      setLoading(false);
    };
    load();
  }, []);

  // ── Refresh tree + stats after mutating pedidos ────────────────────────────
  const refreshTree = useCallback(() => {
    Promise.all([partidasAPI.getTree(), pedidosAPI.getEstadisticas()])
      .then(([treeRes, statsRes]) => {
        setArbol(treeRes.data);
        setArbolError(null);
        setEstadisticas(statsRes.data);
      })
      .catch((err) => {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail || err?.message || 'Error desconocido';
        setArbolError(`Error ${status ?? ''}: ${detail}`);
      });
  }, []);

  // ── Client-side filtering ─────────────────────────────────────────────────
  const pedidosFiltrados = useMemo(() => {
    let result = pedidos;
    if (filtroEstado) result = result.filter((p) => p.estado === filtroEstado);
    if (filtroPartida) result = result.filter((p) => String(p.partida) === filtroPartida);
    if (filtroSolicitadoPor) result = result.filter((p) => String(p.solicitado_por) === filtroSolicitadoPor);
    if (filtroCanal) result = result.filter((p) => p.canal === filtroCanal);
    if (filtroTipo) result = result.filter((p) => p.tipo === filtroTipo);
    if (filtroProveedor) result = result.filter((p) => p.proveedor === filtroProveedor);
    if (filtroBusqueda) {
      const q = filtroBusqueda.toLowerCase();
      result = result.filter(
        (p) =>
          p.nombre?.toLowerCase().includes(q) ||
          p.proveedor?.toLowerCase().includes(q) ||
          p.no_pedido?.toLowerCase().includes(q) ||
          p.no_solicitud?.toLowerCase().includes(q) ||
          p.no_recepcion?.toLowerCase().includes(q) ||
          p.partida_codigo?.toLowerCase().includes(q) ||
          p.partida_nombre?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [pedidos, filtroEstado, filtroPartida, filtroSolicitadoPor, filtroCanal, filtroTipo, filtroProveedor, filtroBusqueda]);

  // Proveedores únicos para el filtro
  const proveedoresUnicos = useMemo(() =>
    [...new Set(pedidos.map((p) => p.proveedor).filter(Boolean))].sort((a, b) =>
      a.localeCompare(b, 'es')
    ),
  [pedidos]);

  // Solicitados por únicos para el filtro
  const solicitadosPorUnicos = useMemo(() => {
    const map = new Map();
    pedidos.forEach((p) => {
      if (p.solicitado_por && p.solicitado_por_nombre) map.set(p.solicitado_por, p.solicitado_por_nombre);
    });
    return [...map.entries()]
      .sort((a, b) => a[1].localeCompare(b[1], 'es'))
      .map(([id, nombre]) => ({ id, nombre }));
  }, [pedidos]);

  // Muestra directamente el nivel 2 en el árbol para ahorrar espacio visual.
  const arbolVisible = useMemo(() => {
    if (!Array.isArray(arbol) || arbol.length === 0) return [];

    const tieneNivel1 = arbol.some((n) => Number(n?.nivel) === 1);
    if (!tieneNivel1) return arbol;

    return arbol.flatMap((n) => {
      if (Number(n?.nivel) === 1 && Array.isArray(n?.hijos)) {
        return n.hijos;
      }
      return [];
    });
  }, [arbol]);

  // ── Drawer actions ─────────────────────────────────────────────────────────
  const openNuevo = () => {
    setEditingPedido(null);
    setDrawerOpen(true);
  };
  const openEditar = useCallback((row) => {
    setEditingPedido(row);
    setDrawerOpen(true);
  }, []);

  const handleSaved = useCallback((saved, esEdicion) => {
    if (esEdicion) {
      setPedidos((prev) => prev.map((p) => (p.id === saved.id ? saved : p)));
    } else {
      setPedidos((prev) => [saved, ...prev]);
    }
    refreshTree();
  }, [refreshTree]);

  // ── Delete ─────────────────────────────────────────────────────────────────
  const handleEliminar = async () => {
    if (!pedidoAEliminar) return;
    setDeleting(true);
    try {
      await pedidosAPI.delete(pedidoAEliminar.id);
      setPedidos((prev) => prev.filter((p) => p.id !== pedidoAEliminar.id));
      setPedidoAEliminar(null);
      refreshTree();
    } finally {
      setDeleting(false);
    }
  };

  // ── DataGrid columns ───────────────────────────────────────────────────────
  const columns = useMemo(
    () => [
      // 1. Identificación principal
      {
        field: 'nombre',
        headerName: 'Pedido',
        flex: 2,
        minWidth: 180,
        align: 'left',
        headerAlign: 'left',
        renderCell: ({ row }) => (
          <Box sx={{ py: 0.75, width: '100%', textAlign: 'left' }}>
            <Typography variant="body2" fontWeight={500} noWrap sx={{ fontSize: 13, lineHeight: 1.4 }}>
              {row.nombre}
            </Typography>
            {row.proveedor && (
              <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: 11, lineHeight: 1.3 }}>
                {row.proveedor}
              </Typography>
            )}
          </Box>
        ),
      },
      // 2. Estado
      {
        field: 'estado',
        headerName: 'Estado',
        width: 145,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ row }) => {
          const s = ESTADO_CHIP_STYLE[row.estado] || {};
          return (
            <Chip
              label={row.estado_display}
              size="small"
              sx={{ bgcolor: s.bg, color: s.color, border: `1px solid ${s.border}`, fontWeight: 600, fontSize: 11 }}
            />
          );
        },
      },
      // 3. Importe
      {
        field: 'importe',
        headerName: 'Importe',
        width: 120,
        type: 'number',
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ value }) => (
          <Typography variant="body2" fontWeight={700} sx={{ fontSize: 13 }}>
            {Number(value || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2, useGrouping: true })}
          </Typography>
        ),
      },
      // 4. Partida presupuestaria
      {
        field: 'partida_nombre',
        headerName: 'Partida',
        flex: 1,
        minWidth: 170,
        align: 'left',
        headerAlign: 'left',
        renderCell: ({ row }) => (
          <Tooltip title={row.partida_nombre || ''} placement="top" arrow>
            <Box sx={{ py: 0.5, width: '100%' }}>
              {row.partida_codigo && (
                <Typography
                  variant="body2"
                  display="block"
                  sx={{ fontSize: 12, fontWeight: 700, fontFamily: 'monospace', color: '#1a237e', lineHeight: 1.4, userSelect: 'text' }}
                >
                  {row.partida_codigo}
                </Typography>
              )}
              {row.partida_nombre && (
                <Typography variant="caption" noWrap display="block" sx={{ fontSize: 11, color: 'text.secondary', lineHeight: 1.3 }}>
                  {row.partida_nombre}
                </Typography>
              )}
            </Box>
          </Tooltip>
        ),
      },
      // 6. Solicitado por
      {
        field: 'solicitado_por_nombre',
        headerName: 'Solicitado por',
        width: 140,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ value }) => (
          <Typography variant="body2" sx={{ fontSize: 12, color: value ? 'text.primary' : 'text.disabled' }}>
            {value || '—'}
          </Typography>
        ),
      },
      // 7. Fecha
      {
        field: 'fecha',
        headerName: 'Fecha',
        width: 90,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ value }) => (
          <Typography variant="body2" sx={{ fontSize: 12, color: value ? 'text.secondary' : 'text.disabled' }}>
            {value
              ? new Date(value + 'T00:00:00').toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: '2-digit' })
              : '—'}
          </Typography>
        ),
      },
      // 8. Canal
      {
        field: 'canal_display',
        headerName: 'Canal',
        width: 100,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ row }) => (
          <Typography variant="body2" sx={{ fontSize: 12 }}>{row.canal_display || '—'}</Typography>
        ),
      },
      // 9. Tipo
      {
        field: 'tipo_display',
        headerName: 'Tipo',
        width: 155,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ row }) => (
          <Typography variant="body2" sx={{ fontSize: 12 }}>{row.tipo_display || '—'}</Typography>
        ),
      },
      // 10-12. Números de referencia (orden natural del ciclo: solicitud → pedido → recepción)
      {
        field: 'no_solicitud',
        headerName: 'Nº Solicitud',
        width: 110,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ value }) => (
          <Typography variant="body2" sx={{ fontSize: 12, fontFamily: 'monospace', color: value ? 'text.primary' : 'text.disabled' }}>
            {value || '—'}
          </Typography>
        ),
      },
      {
        field: 'no_pedido',
        headerName: 'Nº Pedido',
        width: 110,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ value }) => (
          <Typography variant="body2" sx={{ fontSize: 12, fontFamily: 'monospace', color: value ? 'text.primary' : 'text.disabled' }}>
            {value || '—'}
          </Typography>
        ),
      },
      {
        field: 'no_recepcion',
        headerName: 'Nº Recepción',
        width: 115,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ value }) => (
          <Typography variant="body2" sx={{ fontSize: 12, fontFamily: 'monospace', color: value ? 'text.primary' : 'text.disabled' }}>
            {value || '—'}
          </Typography>
        ),
      },
      // 13. Acciones (siempre al final)
      {
        field: 'acciones',
        headerName: '',
        width: 72,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ row }) => (
          <Stack direction="row" spacing={0.25}>
            <Tooltip title="Editar" arrow>
              <IconButton
                size="small"
                onClick={() => openEditar(row)}
                sx={{ color: '#9e9e9e', '&:hover': { color: '#1565c0', bgcolor: '#e3f2fd' } }}
              >
                <EditIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Eliminar" arrow>
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); setPedidoAEliminar(row); }}
                sx={{ color: '#9e9e9e', '&:hover': { color: '#c62828', bgcolor: '#fce4ec' } }}
              >
                <DeleteIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [openEditar]
  );

  const handleTabChange = (_e, newValue) => setActiveTab(newValue);
  const [allExpanded, setAllExpanded] = useState(false);
  const handleToggleExpand = () => {
    const next = !allExpanded;
    setAllExpanded(next);
    setExpandAllAction(next ? 'expand' : 'collapse');
    setExpandAllVersion((v) => v + 1);
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={8}>
        <CircularProgress />
      </Box>
    );
  }

  const KPI_DATA = estadisticas
    ? [
        { label: 'Presupuesto total', value: fmt(estadisticas.total_presupuesto), color: '#546e7a' },
        { label: 'Consumido', value: fmt(estadisticas.total_consumido), color: '#2e7d32' },
        { label: 'Comprometido', value: fmt(estadisticas.total_comprometido), color: '#1565c0' },
        { label: 'Prob. media', value: fmt(estadisticas.total_probabilidad_media), color: '#f57f17' },
        { label: 'Prob. baja', value: fmt(estadisticas.total_probabilidad_baja), color: '#c62828' },
        { label: 'Disponible', value: fmt(estadisticas.disponible_comprometido), color: estadisticas.disponible_comprometido < 0 ? '#c62828' : '#00695c' },
      ]
    : [];

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* ── Page header ── */}
      <Box mb={3}>
        <Typography variant="h5" fontWeight={700} sx={{ letterSpacing: -0.5 }}>
          Control de Presupuesto
        </Typography>
        <Typography variant="body2" color="text.secondary" mt={0.25}>
          Seguimiento de partidas, consumo y pedidos
        </Typography>
      </Box>

      {/* ── KPI cards ── */}
      {KPI_DATA.length > 0 && (
        <Grid container spacing={2} mb={3}>
          {KPI_DATA.map((kpi) => (
            <Grid key={kpi.label} size={{ xs: 6, sm: 4, md: 'auto' }} sx={{ flex: { md: 1 } }}>
              <KpiCard {...kpi} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── Tabs ── */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2,
        borderBottom: 1,
        borderColor: 'divider',
      }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            '& .MuiTabs-flexContainer': { gap: 2 },
            '& .MuiTabs-indicator': { bottom: 0, backgroundColor: '#0d6efd', height: 3 },
            '& .MuiTab-root': { textTransform: 'none', fontSize: '1rem', fontWeight: 600, minHeight: 60 },
            '& .MuiTab-root.Mui-selected': { color: '#0d6efd' },
            flex: 1,
          }}
        >
          <Tab label="Partidas presupuestarias" />
          <Tab label={`Pedidos (${pedidosFiltrados.length})`} />
        </Tabs>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={openNuevo}
          sx={{
            backgroundColor: '#212529',
            color: '#fff',
            borderColor: '#212529',
            fontSize: '0.8rem',
            fontWeight: 'bold',
            px: 2,
            py: 1,
            textTransform: 'none',
            boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
            '&:hover': {
              backgroundColor: '#495057',
              borderColor: '#212529',
              color: '#fff',
              boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
              transform: 'translateY(-1px)',
            },
            width: { xs: '100%', sm: 'auto' },
            whiteSpace: 'nowrap',
            transition: 'all 0.2s ease-in-out',
            mb: { xs: 0, sm: 1 },
          }}
        >
          Nuevo pedido
        </Button>
      </Box>

      {/* Tab: Partidas presupuestarias */}
      <TabPanel value={activeTab} index={0}>
        <Paper
          elevation={0}
          variant="outlined"
          sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}
        >
          {/* Tree header */}
          <Box sx={{ px: 2.5, py: 2, borderBottom: '1px solid #e8e8e8' }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              spacing={1}
              mb={1}
            >
              <Typography variant="subtitle1" fontWeight={700}>
                Partidas presupuestarias
              </Typography>
              <Tooltip title={allExpanded ? 'Contraer todo' : 'Expandir todo'} placement="top" arrow>
                <IconButton
                  size="small"
                  onClick={handleToggleExpand}
                  sx={{
                    color: 'text.secondary',
                    '&:hover': { color: '#3949ab', bgcolor: 'rgba(57,73,171,0.06)' },
                    transition: 'color 0.2s',
                  }}
                >
                  {allExpanded ? <UnfoldLessIcon fontSize="small" /> : <UnfoldMoreIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Box sx={{ width: 16, height: 6, bgcolor: '#2e7d32', borderRadius: 1.5 }} />
                <Typography variant="caption" color="text.secondary">Consumido</Typography>
              </Stack>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Box sx={{ width: 16, height: 6, bgcolor: '#1565c0', borderRadius: 1.5 }} />
                <Typography variant="caption" color="text.secondary">Comprometido</Typography>
              </Stack>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Box sx={{ width: 16, height: 6, bgcolor: '#f57f17', borderRadius: 1.5 }} />
                <Typography variant="caption" color="text.secondary">Prob. media</Typography>
              </Stack>
              <Stack direction="row" spacing={0.75} alignItems="center">
                <Box sx={{ width: 16, height: 6, bgcolor: '#c62828', borderRadius: 1.5 }} />
                <Typography variant="caption" color="text.secondary">Prob. baja</Typography>
              </Stack>
            </Stack>
          </Box>

          {/* Tree body */}
          <Box sx={{ overflowY: 'auto' }}>
            {arbolError && (
              <Alert severity="error" sx={{ m: 2 }}>
                No se pudo cargar el árbol de partidas: {arbolError}
              </Alert>
            )}
            {!arbolError && arbolVisible.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
                No hay partidas presupuestarias configuradas.
              </Typography>
            )}
            {arbolVisible.map((raiz) => (
              <PartidaTreeItem
                key={raiz.id}
                partida={raiz}
                nivel={0}
                expandAllAction={expandAllAction}
                expandAllVersion={expandAllVersion}
              />
            ))}
          </Box>
        </Paper>

      </TabPanel>

      {/* Tab: Pedidos */}
      <TabPanel value={activeTab} index={1}>
        <Paper elevation={0} variant="outlined" sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}>

          {/* ── Toolbar ── */}
          <Box sx={{ px: 2.5, pt: 2, pb: 1.5, borderBottom: '1px solid #eaeef8', bgcolor: '#fafbfd' }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ sm: 'center' }}
              spacing={1.5}
              flexWrap="wrap"
            >
              {/* Search */}
              <TextField
                size="small"
                value={filtroBusqueda}
                onChange={(e) => setFiltroBusqueda(e.target.value)}
                placeholder="Buscar nombre, proveedor…"
                sx={{ minWidth: 230 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
                    </InputAdornment>
                  ),
                  endAdornment: filtroBusqueda ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setFiltroBusqueda('')} edge="end">
                        <CloseIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />

              {/* Estado */}
              <FormControl size="small" sx={{ minWidth: 135 }}>
                <InputLabel>Estado</InputLabel>
                <Select value={filtroEstado} label="Estado" onChange={(e) => setFiltroEstado(e.target.value)}>
                  {ESTADO_OPCIONES.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Partida */}
              <FormControl size="small" sx={{ minWidth: 175 }}>
                <InputLabel>Partida</InputLabel>
                <Select value={filtroPartida} label="Partida" onChange={(e) => setFiltroPartida(e.target.value)}>
                  <MenuItem value="">Todas</MenuItem>
                  {[...hojas].sort((a, b) => a.codigo.localeCompare(b.codigo, 'es')).map((h) => (
                    <MenuItem key={h.id} value={String(h.id)}>
                      <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 700, mr: 0.75 }}>{h.codigo}</Box>
                      <Box component="span" sx={{ color: 'text.secondary', fontSize: 12 }}>{h.nombre}</Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Solicitado por */}
              {solicitadosPorUnicos.length > 0 && (
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Solicitado por</InputLabel>
                  <Select value={filtroSolicitadoPor} label="Solicitado por" onChange={(e) => setFiltroSolicitadoPor(e.target.value)}>
                    <MenuItem value="">Todos</MenuItem>
                    {solicitadosPorUnicos.map(({ id, nombre }) => (
                      <MenuItem key={id} value={String(id)}>{nombre}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              {/* Canal */}
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Canal</InputLabel>
                <Select value={filtroCanal} label="Canal" onChange={(e) => setFiltroCanal(e.target.value)}>
                  <MenuItem value="">Todos</MenuItem>
                  {CANAL_OPCIONES.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Tipo */}
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Tipo</InputLabel>
                <Select value={filtroTipo} label="Tipo" onChange={(e) => setFiltroTipo(e.target.value)}>
                  <MenuItem value="">Todos</MenuItem>
                  {TIPO_OPCIONES.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Proveedor */}
              {proveedoresUnicos.length > 0 && (
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Proveedor</InputLabel>
                  <Select value={filtroProveedor} label="Proveedor" onChange={(e) => setFiltroProveedor(e.target.value)}>
                    <MenuItem value="">Todos</MenuItem>
                    {proveedoresUnicos.map((prov) => (
                      <MenuItem key={prov} value={prov}>{prov}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}

              <Box flex={1} />

            </Stack>

            {/* Active filter chips */}
            {(filtroEstado || filtroPartida || filtroSolicitadoPor || filtroCanal || filtroTipo || filtroProveedor) && (
              <Stack direction="row" spacing={0.75} mt={1.25} alignItems="center" flexWrap="wrap">
                <Typography variant="caption" color="text.disabled" sx={{ fontSize: 11 }}>Filtros:</Typography>
                {filtroEstado && (
                  <Chip
                    label={ESTADO_OPCIONES.find((o) => o.value === filtroEstado)?.label}
                    size="small"
                    onDelete={() => setFiltroEstado('')}
                    sx={{ fontSize: 11, height: 22 }}
                  />
                )}
                {filtroPartida && (
                  <Chip
                    label={hojas.find((h) => String(h.id) === filtroPartida)?.codigo}
                    size="small"
                    onDelete={() => setFiltroPartida('')}
                    sx={{ fontSize: 11, height: 22, bgcolor: '#f0f4ff', color: '#1a237e', border: '1px solid #c5cae9', fontFamily: 'monospace', fontWeight: 700 }}
                  />
                )}
                {filtroSolicitadoPor && (
                  <Chip
                    label={solicitadosPorUnicos.find((s) => String(s.id) === filtroSolicitadoPor)?.nombre}
                    size="small"
                    onDelete={() => setFiltroSolicitadoPor('')}
                    sx={{ fontSize: 11, height: 22 }}
                  />
                )}
                {filtroCanal && (
                  <Chip
                    label={CANAL_OPCIONES.find((o) => o.value === filtroCanal)?.label}
                    size="small"
                    onDelete={() => setFiltroCanal('')}
                    sx={{ fontSize: 11, height: 22 }}
                  />
                )}
                {filtroTipo && (
                  <Chip
                    label={TIPO_OPCIONES.find((o) => o.value === filtroTipo)?.label}
                    size="small"
                    onDelete={() => setFiltroTipo('')}
                    sx={{ fontSize: 11, height: 22 }}
                  />
                )}
                {filtroProveedor && (
                  <Chip
                    label={filtroProveedor}
                    size="small"
                    onDelete={() => setFiltroProveedor('')}
                    sx={{ fontSize: 11, height: 22 }}
                  />
                )}
                <Button
                  size="small"
                  onClick={() => { setFiltroEstado(''); setFiltroPartida(''); setFiltroSolicitadoPor(''); setFiltroBusqueda(''); setFiltroCanal(''); setFiltroTipo(''); setFiltroProveedor(''); }}
                  sx={{ fontSize: 11, py: 0, px: 0.75, color: 'text.secondary', minWidth: 0, textTransform: 'none' }}
                >
                  Limpiar todo
                </Button>
              </Stack>
            )}
          </Box>

          {/* DataGrid */}
          {pedidosFiltrados.length === 0 ? (
            <Box sx={{ p: 8, textAlign: 'center' }}>
              <Typography color="text.secondary" variant="body1">
                {pedidos.length === 0
                  ? 'No hay pedidos registrados todavía.'
                  : 'Ningún pedido coincide con los filtros aplicados.'}
              </Typography>
              {(filtroEstado || filtroPartida || filtroSolicitadoPor || filtroCanal || filtroTipo || filtroProveedor || filtroBusqueda) && (
                <Button
                  size="small"
                  sx={{ mt: 1.5, textTransform: 'none' }}
                  onClick={() => { setFiltroEstado(''); setFiltroPartida(''); setFiltroSolicitadoPor(''); setFiltroBusqueda(''); setFiltroCanal(''); setFiltroTipo(''); setFiltroProveedor(''); }}
                >
                  Limpiar filtros
                </Button>
              )}
            </Box>
          ) : (
            <Box sx={{ height: 480 }}>
            <DataGrid
              rows={pedidosFiltrados}
              columns={columns}
              density="comfortable"
              disableRowSelectionOnClick
              getRowHeight={() => 'auto'}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              pageSizeOptions={[10, 25, 50, 100]}
              sx={{
                height: '100%',
                border: 'none',
                '& .MuiDataGrid-columnHeaders': {
                  bgcolor: '#f8f9fd',
                  borderBottom: '2px solid #eaeef8',
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 700,
                  fontSize: 11,
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: 0.4,
                },
                '& .MuiDataGrid-columnHeader': {
                  justifyContent: 'center',
                },
                '& .MuiDataGrid-columnHeader .MuiDataGrid-columnHeaderTitleContainer': {
                  justifyContent: 'center',
                },
                '& .MuiDataGrid-cell': { borderColor: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', userSelect: 'text', cursor: 'text' },
                '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
                '& .MuiDataGrid-row:hover': { bgcolor: '#f0f4ff' },
                '& .MuiDataGrid-row:nth-of-type(even)': { bgcolor: '#fafbfd' },
                '& .MuiDataGrid-footerContainer': { borderTop: '2px solid #eaeef8', bgcolor: '#fafbfd' },
              }}
            />
            </Box>
          )}
        </Paper>
      </TabPanel>

      {/* ── Drawer: new / edit pedido ── */}
      <PedidoDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        pedido={editingPedido}
        hojas={hojas}
        areasData={areasData}
        onSaved={handleSaved}
      />

      {/* ── Delete confirmation ── */}
      <Dialog
        open={!!pedidoAEliminar}
        onClose={() => !deleting && setPedidoAEliminar(null)}
      >
        <DialogTitle>Eliminar pedido</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Eliminar <strong>{pedidoAEliminar?.nombre}</strong>? Esta acción no se puede
            deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPedidoAEliminar(null)} disabled={deleting}>
            Cancelar
          </Button>
          <Button
            onClick={handleEliminar}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ControlPresupuesto;
